import React, { useEffect, useState } from 'react';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  OverlayDrawer,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { ContractNegotiationConcept } from '../../../models/Entities/Contracts/ContractNegotiationConcept';
import { NewContractNegotiationConcept } from '../../../models/Entities/Contracts/NewContractNegotiationConcept';
import { useContractNegotiationConceptFormStyles } from './ContractNegotiationConceptForm.Styles';

interface Props {
  contractNegotiationId: string;
  contractId: number;
  concept?: ContractNegotiationConcept;
  isOpen: boolean;
  onDismiss: () => void;
  onSave: (entity: NewContractNegotiationConcept | ContractNegotiationConcept) => void;
}

const ContractNegotiationConceptFormComponent = (props: Props) => {
  const { t } = useTranslation();
  const styles = useContractNegotiationConceptFormStyles();

  const [conceptId, setConceptId] = useState<number>(0);
  const [currencyCode, setCurrencyCode] = useState<string>('');
  const [negotiationReferenceCost, setNegotiationReferenceCost] = useState<number>(0);
  const [baselineVariance, setBaselineVariance] = useState<number>(0);

  useEffect(() => {
    if (props.isOpen) {
      setConceptId(props.concept?.conceptId ?? 0);
      setCurrencyCode(props.concept?.currencyCode || '');
      setNegotiationReferenceCost(
        typeof props.concept?.negotiationReferenceCost === 'number'
          ? props.concept.negotiationReferenceCost
          : 0
      );
      setBaselineVariance(
        typeof props.concept?.baselineVariance === 'number' ? props.concept.baselineVariance : 0
      );
    }
  }, [props.isOpen, props.concept]);

  const handleSave = () => {
    const base: NewContractNegotiationConcept = {
      contractNegotiationId: props.contractNegotiationId,
      conceptId,
      currencyCode: currencyCode || undefined,
      negotiationReferenceCost,
      baselineVariance,
    };

    if (props.concept?.id) {
      props.onSave({ ...props.concept, ...base });
    } else {
      props.onSave(base);
    }
  };

  const isEdit = !!props.concept?.id;
  const dialogTitle = isEdit
    ? t('contractNegotiationConceptForm.titleEdit', 'Editar concepto de negociación')
    : t('contractNegotiationConceptForm.titleNew', 'Nuevo concepto de negociación');

  return (
    <OverlayDrawer position="end" size="medium" open={props.isOpen}
      onOpenChange={(_, { open }) => { if (!open) props.onDismiss(); }}>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={<Button appearance="subtle" icon={<DismissRegular />} onClick={props.onDismiss} />}>
          {dialogTitle}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.form}>
          <div className={styles.formRow}>
            <Field label={t('contractNegotiationConceptForm.fields.conceptId', 'ID Concepto')}>
              <Input
                type="number"
                value={String(conceptId)}
                onChange={(_, data) => setConceptId(Number(data.value))}
              />
            </Field>
            <Field label={t('contractNegotiationConceptForm.fields.currencyCode', 'Código de moneda')}>
              <Input
                value={currencyCode}
                onChange={(_, data) => setCurrencyCode(data.value)}
                placeholder="ARS"
              />
            </Field>
          </div>
          <div className={styles.formRow}>
            <Field label={t('contractNegotiationConceptForm.fields.referenceCost', 'Costo de referencia')}>
              <Input
                type="number"
                value={String(negotiationReferenceCost)}
                onChange={(_, data) => setNegotiationReferenceCost(Number(data.value))}
              />
            </Field>
            <Field label={t('contractNegotiationConceptForm.fields.baselineVariance', 'Varianza de base (%)')}>
              <Input
                type="number"
                value={String(baselineVariance)}
                onChange={(_, data) => setBaselineVariance(Number(data.value))}
              />
            </Field>
          </div>
        </div>
        <div className={styles.formActions}>
          <Button appearance="secondary" onClick={props.onDismiss}>
            {t('contractNegotiationConceptForm.actions.cancel', 'Cancelar')}
          </Button>
          <Button appearance="primary" onClick={handleSave}>
            {t('contractNegotiationConceptForm.actions.save', 'Guardar')}
          </Button>
        </div>
      </DrawerBody>
    </OverlayDrawer>
  );
};

export default ContractNegotiationConceptFormComponent;
